// Step 3 of the missing-item flow: confirm the count before it resolves.
//
// Pressing "Done counting" used to resolve immediately — the line was flagged
// missing, or re-slotted, or satisfied, with nothing but a toast afterwards to
// say which. This is the same confirm the Stock Check module puts in front of
// completing a check, for the same reasons: the picker sees what their count
// says before it becomes a decision, and the "these weren't found — take them
// off the shelf?" choice stays theirs rather than being implied by silence.
//
// Two things are being confirmed, and they are deliberately separate:
//
//   1. The target SKU. Found → the line goes back on the pick list and they
//      scan it into the tote. Not found → the system looks for stock
//      elsewhere, and flags it missing for dispatch if there is none. Nothing
//      to opt into: the flow exists because this item is in question.
//
//   2. Everything ELSE at the location that the count didn't turn up. Only
//      offered when there is an unreserved shortfall, because reserved units
//      that went unscanned may be in a tote already and are never treated as
//      loss (same rule as the module's write-off split).
import { X, PackageSearch, MapPin } from "lucide-react";
import { useTranslation } from "../../i18n";

export interface UncountedLine {
    sku: string;
    title: string;
    /** Unreserved shortfall — the only part that can be taken off the shelf. */
    qty: number;
}

interface Props {
    location: string;
    sku: string;
    /** Units of the target the count turned up. */
    foundQty: number;
    /** Units the line still needs. */
    requiredQty: number;
    /** Other SKUs at this location with an unreserved shortfall. */
    uncounted: UncountedLine[];
    busy?: boolean;
    onCancel: () => void;
    /** removeUncounted answers question 2 above. */
    onConfirm: (removeUncounted: boolean) => void;
}

const MissingFinishConfirm = ({
    location, sku, foundQty, requiredQty, uncounted, busy, onCancel, onConfirm,
}: Props) => {
    const { t } = useTranslation();
    const found = requiredQty > 0 && foundQty >= requiredQty;
    const removable = uncounted.reduce((n, l) => n + l.qty, 0);

    return (
        <div className="modal-overlay" onClick={() => !busy && onCancel()}>
            <div className="modal-card stock-check-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{t("picking_missing_confirm_title")}</h2>
                    <button className="modal-close" onClick={onCancel} disabled={busy}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    {/* What the count says about the item that sent them here,
                        and what happens to it next. */}
                    <div className={"picking-missing-confirm-target"
                        + (found ? " picking-missing-confirm-target--found" : "")}>
                        <PackageSearch size={18} />
                        <div>
                            <div className="picking-missing-confirm-target-line">
                                {found
                                    ? t("picking_missing_confirm_found", {
                                        count: String(foundQty), sku,
                                    })
                                    : t("picking_missing_confirm_not_found", {
                                        count: String(foundQty), required: String(requiredQty), sku,
                                    })}
                            </div>
                            <div className="stock-check-modal-hint">
                                {found
                                    ? t("picking_missing_confirm_found_next")
                                    : t("picking_missing_confirm_not_found_next")}
                            </div>
                        </div>
                    </div>

                    {uncounted.length > 0 && (
                        <>
                            <p className="stock-check-modal-hint picking-missing-confirm-others">
                                <MapPin size={14} />
                                {" "}{t("picking_missing_confirm_others_body", {
                                    count: String(removable), location,
                                })}
                            </p>
                            <div className="stock-check-missing-list">
                                {uncounted.map(l => (
                                    <div key={l.sku} className="stock-check-missing-row">
                                        <span className="stock-check-missing-title">{l.title}</span>
                                        <span className="stock-check-missing-sku">{l.sku}</span>
                                        <span className="stock-check-missing-qty">−{l.qty}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    <div className={"modal-confirm-buttons"
                        + (uncounted.length > 0 ? " modal-confirm-buttons--stacked" : "")}>
                        <button
                            type="button"
                            className="btn btn-ghost btn-lg"
                            onClick={onCancel}
                            disabled={busy}
                        >
                            {t("picking_missing_confirm_keep_counting")}
                        </button>
                        {uncounted.length > 0 ? (
                            <>
                                {/* Keep — the picker counted their own item and
                                    nothing else; the rest of the shelf is
                                    untouched. */}
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-lg"
                                    onClick={() => onConfirm(false)}
                                    disabled={busy}
                                >
                                    {t("picking_missing_confirm_keep_others")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger btn-lg"
                                    onClick={() => onConfirm(true)}
                                    disabled={busy}
                                >
                                    {busy
                                        ? t("picking_missing_finishing")
                                        : t("picking_missing_confirm_remove_others", {
                                            count: String(removable),
                                        })}
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-success btn-lg"
                                onClick={() => onConfirm(false)}
                                disabled={busy}
                            >
                                {busy ? t("picking_missing_finishing") : t("picking_missing_confirm_finish")}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MissingFinishConfirm;
